import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PasswordResetPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset`, { email });
      setSubmitted(true);
      toast.success("Reset instructions sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="luxury-card p-8 md:p-10 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/20 flex items-center justify-center mb-6">
              <Mail className="w-8 h-8 text-[#C9A962]" />
            </div>
            
            <h2 className="text-2xl font-display font-medium text-white mb-3">Check Your Email</h2>
            <p className="text-[#9A9A9A] mb-8 leading-relaxed">
              If an account exists with <strong className="text-[#C9A962]">{email}</strong>, you will receive a password reset token shortly.
            </p>
            
            <div className="space-y-4">
              <Link to="/password-reset/confirm">
                <Button className="w-full h-12 bg-gradient-to-r from-[#C9A962] to-[#A68B4B] hover:opacity-90 text-[#0A0A0A] font-semibold text-xs tracking-widest uppercase">
                  Enter Reset Token
                </Button>
              </Link>
              
              <Link to="/login">
                <Button variant="outline" className="w-full h-12 border-[#252525] text-[#9A9A9A] hover:text-white hover:border-[#C9A962] bg-transparent">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-display font-medium text-gradient-luxury">OCULUS</h1>
              <p className="text-[#C9A962] text-xs tracking-[0.2em] font-elegant">ROYALE</p>
            </div>
          </div>
        </div>

        <div className="luxury-card p-8 md:p-10">
          <div className="mb-8">
            <h2 className="text-2xl font-display font-medium text-white mb-2">Reset Password</h2>
            <p className="text-[#9A9A9A] text-sm leading-relaxed">
              Enter your email address and we&apos;ll send you a reset token to create a new password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#9A9A9A] text-xs uppercase tracking-wider">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#C9A962] to-[#A68B4B] hover:opacity-90 text-[#0A0A0A] font-semibold text-xs tracking-widest uppercase"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Instructions"
              )}
            </Button>
          </form>

          <div className="luxury-divider my-8">or</div>

          <div className="text-center">
            <Link to="/login">
              <Button variant="ghost" className="text-[#9A9A9A] hover:text-white hover:bg-[#1A1A1A]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
